import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import axios from 'axios';

import { isAuthEnabled, supabase } from '../lib/supabase';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8001';

// Types
export interface Profile {
  id: string;
  email: string;
  role: 'user' | 'admin';
  activated: boolean;
  searches_used: number;
  search_quota: number | null; // null = unlimited (legacy)
  searches_remaining: number | null;
  // Credits (internal currency)
  credit_balance?: number;
  search_cost_credits?: number;
  unlimited?: boolean;
  // Subscription (Lemon Squeezy)
  subscription_status?: string | null;
  subscription_variant_id?: string | null;
  subscription_renews_at?: string | null;
  subscription_active?: boolean;
}

/** Identity details from the Supabase session (Google metadata when present). */
export interface AuthUser {
  name: string | null;
  avatarUrl: string | null;
}

interface AuthContextType {
  /** Backend profile (role / activation / quota). Null until loaded. */
  profile: Profile | null;
  /** Display identity from the auth session (name/avatar). Null in dev mode. */
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** False when Supabase env vars are absent → legacy dev identity. */
  isAuthEnabled: boolean;
  login: (email: string, password: string, captchaToken?: string | null) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (email: string, password: string, captchaToken?: string | null) => Promise<string>;
  resetPassword: (email: string, captchaToken?: string | null) => Promise<void>;
  updatePassword: (newPassword: string, currentPassword?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEV_PROFILE: Profile = {
  id: 'anonymous',
  email: 'dev@localhost',
  role: 'admin',
  activated: true,
  searches_used: 0,
  search_quota: null,
  searches_remaining: null,
};

async function fetchProfile(token: string): Promise<Profile | null> {
  try {
    const resp = await axios.get<Profile>(`${API_BASE}/api/v1/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return resp.data;
  } catch (e) {
    console.error('Failed to load profile:', e);
    return null;
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [hasSession, setHasSession] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const refreshProfile = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!isAuthEnabled || !token) return;
    const p = await fetchProfile(token);
    if (p) setProfile(p);
  }, []);

  useEffect(() => {
    if (!isAuthEnabled || !supabase) {
      // Dev mode: legacy localStorage identity, everything open
      setProfile(DEV_PROFILE);
      setHasSession(true);
      setIsLoading(false);
      return;
    }

    // Keep localStorage token in sync with the Supabase session; the axios
    // interceptor in api/decision.ts and authHeaders() read it from there.
    const applySession = async (session: { access_token: string; user?: { user_metadata?: Record<string, unknown> } } | null) => {
      const token = session?.access_token ?? null;
      if (token) {
        localStorage.setItem('auth_token', token);
        setHasSession(true);
        const meta = session?.user?.user_metadata ?? {};
        setUser({
          name: (meta.full_name as string) || (meta.name as string) || null,
          avatarUrl: (meta.avatar_url as string) || (meta.picture as string) || null,
        });
        const p = await fetchProfile(token);
        setProfile(p);
      } else {
        localStorage.removeItem('auth_token');
        setHasSession(false);
        setProfile(null);
        setUser(null);
      }
      setIsLoading(false);
    };

    supabase.auth.getSession().then(({ data }) => {
      applySession(data.session ?? null);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session ?? null);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string, captchaToken?: string | null) => {
    if (!supabase) throw new Error('Auth is disabled in dev mode');
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: captchaToken ? { captchaToken } : undefined,
    });
    if (error) throw new Error(error.message);
  };

  const loginWithGoogle = async () => {
    if (!supabase) throw new Error('Auth is disabled in dev mode');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/app` },
    });
    if (error) throw new Error(error.message);
  };

  const register = async (email: string, password: string, captchaToken?: string | null) => {
    try {
      // Registration goes through our API, not GoTrue, so Supabase's own
      // captcha setting never sees it; the token is verified server-side.
      const resp = await axios.post(`${API_BASE}/api/v1/auth/register`, {
        email,
        password,
        captcha_token: captchaToken ?? null,
      });
      return resp.data.message as string;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response) {
        const detail = e.response.data?.detail;
        throw new Error(
          typeof detail === 'string' ? detail : detail?.message || 'Registration failed'
        );
      }
      throw e;
    }
  };

  const resetPassword = async (email: string, captchaToken?: string | null) => {
    if (!supabase) throw new Error('Auth is disabled in dev mode');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
      ...(captchaToken ? { captchaToken } : {}),
    });
    if (error) throw new Error(error.message);
  };

  const updatePassword = async (newPassword: string, currentPassword?: string) => {
    if (!supabase) throw new Error('Auth is disabled in dev mode');
    // Changing a password used to need nothing but an open tab, so a stolen
    // session could lock the owner out permanently. Proving the current
    // password costs the legitimate user one field and costs a thief the
    // whole attack. Supabase's own reauth setting is not used: it mails a
    // nonce instead, which is worse to use and cannot check the old password.
    if (currentPassword) {
      const { data: current } = await supabase.auth.getUser();
      const email = current.user?.email;
      if (!email) throw new Error('No signed-in account to verify against');
      const { error: reauthError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });
      if (reauthError) throw new Error('Current password is incorrect');
    }
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw new Error(error.message);
  };

  const logout = async () => {
    if (supabase) await supabase.auth.signOut();
    localStorage.removeItem('auth_token');
    setProfile(null);
    setUser(null);
    setHasSession(false);
  };

  return (
    <AuthContext.Provider
      value={{
        profile,
        user,
        isAuthenticated: hasSession,
        isLoading,
        isAuthEnabled,
        login,
        loginWithGoogle,
        register,
        resetPassword,
        updatePassword,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
